import { createHashRouter, createRoutesFromElements, Route } from 'react-router-dom';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import AppLayout from '@/layout/AppLayout';
import { suspendPageLoad } from '@/routes/suspendPageLoad';

// Lazy load pages for better performance and automatically split the bundle accordingly
const DashboardPage = suspendPageLoad(() => import('@/pages/dashboard/Dashboard'));
const AdministrationPage = suspendPageLoad(() => import('@/pages/administration/AdministrationPage'));
const ChargingCardsPage = suspendPageLoad(() => import('@/pages/chargingCards/ChargingCardsPage'));
const CardsPage = suspendPageLoad(() => import('@/features/chargingCards/cards/ChargingCards'));
const HistoryPage = suspendPageLoad(() => import('@/features/chargingCards/history/OrderHistory'));
const ContractPage = suspendPageLoad(() => import('@/features/administration/ChargingContract'));
const InvoicesPage = suspendPageLoad(() => import('@/pages/invoices/Invoices'));
const ChargingActivity = suspendPageLoad(() => import('@/features/chargingActivity/ChargingActivity'));
const SignupPage = suspendPageLoad(() => import('@/pages/signup/Signup'));
const NotFound = suspendPageLoad(() => import('@/pages/NotFound'));
const NoPermissions = suspendPageLoad(() => import('@/pages/NoPermissions'));
const NotBookedState = suspendPageLoad(() => import('@/pages/NotBooked'));

export const DEFAULT_ROUTE = '/dashboard';
export const ADMINISTRATION = '/administration';
export const ADMINISTRATION_CARDS = '/administration/cards';
export const ADMINISTRATION_HISTORY = '/administration/history';
export const ADMINISTRATION_CONTRACT = '/administration/contract';
export const CHARGING_CARDS = '/charging-cards';
export const CHARGING_CARDS_MANAGEMENT = '/charging-cards/management';
export const CHARGING_CARDS_ORDER_HISTORY = '/charging-cards/order-history';
export const INVOICES = '/invoices';
export const CHARGING_ACTIVITY = '/charging/activity';
export const SIGNUP = '/signup';
export const NOT_FOUND = '/not-found';
export const NO_PERMISSIONS = '/unauthorized';
export const NOT_AVAILABLE = '/not-available';

export const routes = [
    DEFAULT_ROUTE,
    ADMINISTRATION,
    ADMINISTRATION_CARDS,
    ADMINISTRATION_HISTORY,
    ADMINISTRATION_CONTRACT,
    CHARGING_CARDS,
    CHARGING_CARDS_MANAGEMENT,
    CHARGING_CARDS_ORDER_HISTORY,
    INVOICES,
    CHARGING_ACTIVITY,
    SIGNUP,
    NOT_FOUND,
    NO_PERMISSIONS,
    NOT_AVAILABLE,
];

export const permissionsMap = new Map<string, string>([
    [DEFAULT_ROUTE, 'emsp-dashboard'],
    [ADMINISTRATION, 'emsp-cards.read'],
    [ADMINISTRATION_CARDS, 'emsp-cards.read'],
    [ADMINISTRATION_HISTORY, 'emsp-cards.write'],
    [ADMINISTRATION_CONTRACT, 'emsp-contract.read'],
    [CHARGING_CARDS, 'emsp-cards.read'],
    [CHARGING_CARDS_MANAGEMENT, 'emsp-cards.read'],
    [CHARGING_CARDS_ORDER_HISTORY, 'emsp-cards.write'],
    [INVOICES, 'emsp-invoices.read'],
    [CHARGING_ACTIVITY, 'emsp-charging-sessions.read'],
    [SIGNUP, 'emsp-onboarding'],
    [NOT_FOUND, 'emsp-dashboard'],
]);

export const pageTitles: { page: string; title: string }[] = [
    { page: DEFAULT_ROUTE, title: 'Overview' },
    { page: ADMINISTRATION_CARDS, title: 'Administration Charging Cards' },
    { page: ADMINISTRATION_HISTORY, title: 'Administration Order History' },
    { page: ADMINISTRATION_CONTRACT, title: 'Administration Charging Contract' },
    { page: CHARGING_CARDS_MANAGEMENT, title: 'Charging Cards Card Management' },
    { page: CHARGING_CARDS_ORDER_HISTORY, title: 'Charging Cards Order History' },
    { page: INVOICES, title: 'Invoices' },
    { page: CHARGING_ACTIVITY, title: 'Charging Activity' },
    { page: SIGNUP, title: 'Sign up' },
];

export const router = createHashRouter(
    createRoutesFromElements(
        <Route path='/' element={<AppLayout />}>
            <Route path={DEFAULT_ROUTE} errorElement={<ErrorBoundary />} element={<DashboardPage />} />
            <Route path={ADMINISTRATION} element={<AdministrationPage />} errorElement={<ErrorBoundary />}>
                <Route index element={<CardsPage />} />
                <Route path={ADMINISTRATION_CARDS} element={<CardsPage />} />
                <Route path={ADMINISTRATION_HISTORY} element={<HistoryPage />} />
                <Route path={ADMINISTRATION_CONTRACT} element={<ContractPage />} />
            </Route>
            <Route path={CHARGING_CARDS} element={<ChargingCardsPage />} errorElement={<ErrorBoundary />}>
                <Route index element={<CardsPage />} />
                <Route path={CHARGING_CARDS_MANAGEMENT} element={<CardsPage />} />
                <Route path={CHARGING_CARDS_ORDER_HISTORY} element={<HistoryPage />} />
            </Route>
            <Route path={INVOICES} errorElement={<ErrorBoundary />} element={<InvoicesPage />} />
            <Route path={CHARGING_ACTIVITY} errorElement={<ErrorBoundary />} element={<ChargingActivity />} />
            <Route path={SIGNUP} errorElement={<ErrorBoundary />} element={<SignupPage />} />
            <Route path={NOT_FOUND} errorElement={<ErrorBoundary />} element={<NotFound />} />
            <Route path={NO_PERMISSIONS} errorElement={<ErrorBoundary />} element={<NoPermissions />} />
            <Route path={NOT_AVAILABLE} errorElement={<ErrorBoundary />} element={<NotBookedState />} />
            <Route path='*' element={<NotFound />} />
        </Route>
    )
);
